import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * CollapsibleSection - Reusable collapsible panel for organizing content
 * 
 * Used in Results page to create information hierarchy:
 * - Level 1: Always visible (Deal Decision, Net Profit, ARV, Confidence)
 * - Level 2: Concise explanations (Why This Deal, Risks, Actions)
 * - Level 3: Detailed data (Comparables, Financials, Assumptions)
 */
const CollapsibleSection = ({ 
  title, 
  icon: Icon, 
  children, 
  defaultOpen = false,
  badge = null,
  testId = null,
  variant = 'default' // 'default', 'success', 'warning', 'danger'
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const variantStyles = {
    default: 'border-border',
    success: 'border-green-200 bg-green-50/30',
    warning: 'border-amber-200 bg-amber-50/30',
    danger: 'border-red-200 bg-red-50/30',
  };
  
  const headerVariantStyles = {
    default: 'hover:bg-muted/50',
    success: 'hover:bg-green-100/50',
    warning: 'hover:bg-amber-100/50',
    danger: 'hover:bg-red-100/50',
  };
  
  return (
    <div 
      className={`rounded-xl border overflow-hidden ${variantStyles[variant]} transition-all duration-200`}
      data-testid={testId}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-4 text-left transition-colors ${headerVariantStyles[variant]}`}
        aria-expanded={isOpen}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
          <span className="font-semibold text-foreground">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
              {badge}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {/* Collapsible content with smooth animation */}
      <div 
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 pt-0 border-t border-border/50">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
